import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { Shift } from "~/providers/shift-provider";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useShiftStore } from "~/stores/use-shift-store";
import { useShiftManager } from "../../managers/use-shift-manager";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import ShiftForm from "./shift-form";

export default function ShiftDialog() {
  const { t } = useTranslation("common");
  const { deleteShift } = useShiftManager();

  const isMobile = useMediaQuery(768);
  const { shiftFormOpen, setShiftFormOpen, currentShift, setCurrentShift } =
    useShiftStore();

  const handleDialog = (open: boolean) => {
    setShiftFormOpen(open);
    if (open) {
      setCurrentShift(undefined);
    }
  };

  const handleDelete = async (shift: Shift) => {
    const result = await deleteShift(shift);

    if (result === true) {
      handleDialog(false);
    }
  };

  if (isMobile) {
    return (
      <Drawer open={shiftFormOpen} onOpenChange={handleDialog}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {currentShift?.serviceName && (
                <div className="flex flex-row items-center gap-2">
                  <span>{currentShift.serviceName}</span>
                  <span className="text-xs font-normal">{`${format(currentShift.startTime, "p")} - ${format(currentShift.endTime, "p")}`}</span>
                </div>
              )}
              {!currentShift?.serviceName && <>{t("new_shift")}</>}
            </DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[80vh] min-h-[80vh] small-scrollbar overflow-auto flex flex-col">
            <ShiftForm
              shift={currentShift}
              onClose={() => handleDialog(false)}
            />
          </div>
          <DrawerFooter>
            <div className="flex flex-row gap-2 justify-between w-full">
              {currentShift && (
                <Button
                  className="w-full"
                  onClick={() => {
                    void handleDelete(currentShift);
                  }}
                  variant="destructive"
                >
                  {t("delete")}
                </Button>
              )}
              <Button
                className="w-full"
                form="shift-form"
                type="submit"
                variant="success"
              >
                {currentShift ? t("update") : t("create")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={shiftFormOpen} onOpenChange={handleDialog}>
      <DialogContent className="max-w-4xl w-full min-h-[60vh]">
        <DialogHeader>
          <DialogTitle>
            {currentShift?.serviceName && (
              <div className="flex flex-row items-center gap-2">
                <span>{currentShift.serviceName}</span>
                <span className="text-xs font-normal">{`${format(currentShift.startTime, "p")} - ${format(currentShift.endTime, "p")}`}</span>
              </div>
            )}
            {!currentShift?.serviceName && <>{t("new_shift")}</>}
          </DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="max-h-[80vh] min-h-[80vh] small-scrollbar overflow-auto flex flex-col">
          <ShiftForm shift={currentShift} onClose={() => handleDialog(false)} />
        </div>
        <DialogFooter>
          <div className="flex flex-row gap-2 justify-between w-full">
            {currentShift && (
              <Button
                className="w-full"
                onClick={() => {
                  void handleDelete(currentShift);
                }}
                variant="destructive"
              >
                {t("delete")}
              </Button>
            )}
            <Button
              className="w-full"
              form="shift-form"
              type="submit"
              variant="success"
            >
              {currentShift ? t("update") : t("create")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
